import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaArrowRight,
  FaEnvelope,
  FaFilter,
  FaPen,
  FaPlus,
  FaSearch,
  FaShieldAlt,
  FaTimes,
  FaTrash,
  FaUsers,
} from "react-icons/fa";
import { AuthContext } from "../AuthProvider";
import PageTemplate from "../components/PageTemplate";
import SlidePageTransition from "../components/transitions/SlidePageTransition";
import Modal from "../components/Modal";
import CopyButton from "../components/CopyButton";
import ErrorPage from "../components/ErrorPage";
import Spinner from "../components/Spinner";
import { useCan } from "../authz/useCan";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { deleteParent, fetchParents } from "./parents/api";
import type { AppError, EmailFilter, NewsletterFilter, Parent, PortalFilter } from "./parents/types";
import {
  filterParents,
  newsletterStatusForFilter,
  portalStatusForFilter,
} from "./parents/utils/parentList";
import { getBackendEmailAssessment } from "./parents/utils/parentStatus";
import {
  navigateParentFlow,
  parentCreatePath,
  parentDetailsPath,
  parentEditPath,
} from "./parents/navigation";
import {
  getCachedParents,
  getParentOverviewUiState,
  hasParentOverviewCache,
  loadParentOverview,
  removeCachedParent,
  setCachedParents,
  setParentOverviewUiState,
} from "./parents/cache";
import {
  ParentMergeWizardModal,
  loadParentMergeCandidate,
  mergeParents,
} from "../features/parentMerge";

type BadgeTone = "success" | "warn" | "danger" | "neutral";

interface BadgeProps {
  children: React.ReactNode;
  tone?: BadgeTone;
  title?: string;
}

const initialsFor = (parent: Parent) =>
  [parent.firstName, parent.lastName]
    .filter(Boolean)
    .map((part) => String(part).trim().charAt(0).toUpperCase())
    .join("")
    .slice(0, 2) || "?";


const Badge = ({ children, tone = "neutral", title }: BadgeProps) => {
  const tones: Record<BadgeTone, string> = {
    success:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-200",
    warn:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200",
    danger:
      "border-red-200 bg-red-50 text-red-700 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-200",
    neutral:
      "border-slate-200 bg-slate-50 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${tones[tone]}`}
      title={title}
    >
      {children}
    </span>
  );
};

const PortalBadge = ({ parent }: { parent: Parent }) => {
  const status = portalStatusForFilter(parent);
  if (status === "inactive") return <Badge tone="danger">Inactive</Badge>;
  if (status === "active") return <Badge tone="success">Active</Badge>;
  return <Badge tone="warn">Pending</Badge>;
};

const NewsletterBadge = ({ parent }: { parent: Parent }) => {
  const status = newsletterStatusForFilter(parent);
  if (status === "opted_out") return <Badge tone="warn" title="Parent has opted out">Opted out</Badge>;
  if (status === "subscribed") return <Badge tone="success">Subscribed</Badge>;
  if (status === "unsubscribed") return <Badge tone="warn">Unsubscribed</Badge>;
  return <Badge tone="neutral">Ineligible</Badge>;
};

const toggleFilterValue = <T extends string>(current: T[], value: T): T[] => {
  const next = new Set(current);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return Array.from(next);
};

const Parents = () => {
  const { keycloak } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cachedUiState = useMemo(() => getParentOverviewUiState(), []);
  const cachedParents = useMemo(() => getCachedParents(), []);
  const [parents, setParents] = useState<Parent[]>(() => cachedParents ?? []);
  const [loading, setLoading] = useState(() => !hasParentOverviewCache());
  const [showInitialSpinner, setShowInitialSpinner] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  const [searchQuery, setSearchQuery] = useState(cachedUiState.searchQuery);
  const [filterOpen, setFilterOpen] = useState(false);
  const [emailFilters, setEmailFilters] = useState<EmailFilter[]>(() => [...cachedUiState.emailFilters]);
  const [portalFilters, setPortalFilters] = useState<PortalFilter[]>(() => [...cachedUiState.portalFilters]);
  const [newsletterFilters, setNewsletterFilters] = useState<NewsletterFilter[]>(() => [...cachedUiState.newsletterFilters]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [parentToDelete, setParentToDelete] = useState<string | null>(null);
  const [showRotateOverlay, setShowRotateOverlay] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);
  const [mergeSourceId, setMergeSourceId] = useState<string | null>(null);
  const [mergeCandidates, setMergeCandidates] = useState<any>(null);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const restoredScrollRef = useRef(false);
  const filterPopoverRef = useRef<HTMLDivElement | null>(null);

  const canManageFamiliesLegacy = useCan("families", "crud");
  const canManageFamiliesDirect = useCan("families", "crud");
  const canManageParentsCrud = useCan("family.parents", "crud");
  const canManageParentsManage = useCan("family.parents", "manage");
  const canManageFamilies = canManageFamiliesLegacy || canManageFamiliesDirect || canManageParentsCrud || canManageParentsManage;
  const canViewFamiliesLegacy = useCan("families", "read");
  const canViewFamiliesDirect = useCan("families", "read");
  const canListFamiliesDirect = useCan("families", "list");
  const canViewFamilies = canViewFamiliesLegacy || canViewFamiliesDirect || canListFamiliesDirect;

  useEffect(() => {
    let cancelled = false;
    const loadParents = async () => {
      if (!keycloak?.token) {
        setError({ message: t("common.auth_required"), status: 401 });
        setLoading(false);
        return;
      }
      try {
        const data = await loadParentOverview(canViewFamilies);
        if (!cancelled) setParents(data);
      } catch (loadError) {
        if (!cancelled && !hasParentOverviewCache()) {
          setError(mapHttpErrorToMessage(loadError, t) as AppError);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadParents();
    return () => {
      cancelled = true;
    };
  }, [keycloak?.token, t, canViewFamilies]);

  useEffect(() => {
    if (!loading) {
      setShowInitialSpinner(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowInitialSpinner(true), 300);
    return () => window.clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    setParentOverviewUiState({
      searchQuery,
      emailFilters,
      portalFilters,
      newsletterFilters,
    });
  }, [searchQuery, emailFilters, portalFilters, newsletterFilters]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!restoredScrollRef.current && hasParentOverviewCache()) {
      restoredScrollRef.current = true;
      window.requestAnimationFrame(() => window.scrollTo({ top: getParentOverviewUiState().scrollY }));
    }

    let ticking = false;
    const persistScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setParentOverviewUiState({ scrollY: window.scrollY });
        ticking = false;
      });
    };
    window.addEventListener("scroll", persistScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", persistScroll);
      setParentOverviewUiState({ scrollY: window.scrollY });
    };
  }, []);

  useEffect(() => {
    if (!filterOpen || typeof document === "undefined") return undefined;

    const closeOnOutsideInteraction = (event: MouseEvent | FocusEvent) => {
      const target = event.target;
      if (target instanceof Node && filterPopoverRef.current?.contains(target)) return;
      setFilterOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFilterOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideInteraction);
    document.addEventListener("focusin", closeOnOutsideInteraction);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideInteraction);
      document.removeEventListener("focusin", closeOnOutsideInteraction);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [filterOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const updateOverlay = () => {
      const width = window.innerWidth;
      const isMobileLayout = width < 1024;
      const isPortrait = window.matchMedia?.("(orientation: portrait)")?.matches ?? false;
      setShowRotateOverlay(isMobileLayout && isPortrait);
    };
    updateOverlay();
    window.addEventListener("resize", updateOverlay);
    window.addEventListener("orientationchange", updateOverlay);
    return () => {
      window.removeEventListener("resize", updateOverlay);
      window.removeEventListener("orientationchange", updateOverlay);
    };
  }, []);

  const filteredParents = useMemo(
    () => filterParents(parents, searchQuery, emailFilters, portalFilters, newsletterFilters),
    [parents, searchQuery, emailFilters, portalFilters, newsletterFilters]
  );

  const activeFilterCount = emailFilters.length + portalFilters.length + newsletterFilters.length;

  const confirmDeleteParent = (parentId: string) => {
    if (!canManageFamilies) return;
    setParentToDelete(parentId);
    setShowConfirm(true);
  };

  const handleDeleteParent = async () => {
    try {
      if (!keycloak?.token || !parentToDelete) {
        setError({ message: t("common.auth_required"), status: 401 });
        return;
      }
      await deleteParent(parentToDelete);
      setParents((current) => current.filter((parent) => parent.id !== parentToDelete));
      removeCachedParent(parentToDelete);
      setShowConfirm(false);
      setParentToDelete(null);
    } catch (deleteError) {
      setError(mapHttpErrorToMessage(deleteError, t) as AppError);
    }
  };

  const clearFilters = () => {
    setEmailFilters([]);
    setPortalFilters([]);
    setNewsletterFilters([]);
  };

  const toggleMergeParent = (parentId: string) => {
    if (!canManageFamilies || mergeLoading) return;
    if (mergeTargetId === parentId) {
      setMergeTargetId(mergeSourceId);
      setMergeSourceId(null);
      setMergeError(null);
      return;
    }
    if (mergeSourceId === parentId) {
      setMergeSourceId(null);
      setMergeError(null);
      return;
    }
    if (!mergeTargetId) {
      setMergeTargetId(parentId);
      setMergeError(null);
      return;
    }
    if (!mergeSourceId) {
      setMergeSourceId(parentId);
      setMergeError(null);
    }
  };

  const compareParents = async () => {
    if (!mergeTargetId || !mergeSourceId || mergeTargetId === mergeSourceId) return;
    setMergeLoading(true);
    setMergeError(null);
    try {
      const [target, source] = await Promise.all([
        loadParentMergeCandidate(mergeTargetId),
        loadParentMergeCandidate(mergeSourceId),
      ]);
      // ParentDTO deliberately does not expose keycloakId. Two enabled portal accounts
      // therefore require explicit server-side resolution before either record may be removed.
      if (target.parent?.accountEnabled && source.parent?.accountEnabled) {
        setMergeError(
          t("parentMerge.keycloakConflict", {
            defaultValue:
              "Both parents have a different portal account. Resolve the Keycloak conflict before merging.",
          })
        );
        return;
      }
      setMergeCandidates({ target, source });
    } catch (loadError) {
      setMergeError(
        (loadError as any)?.response?.data?.message ||
          t("parentMerge.loadFailed", { defaultValue: "Could not load the selected parents." })
      );
    } finally {
      setMergeLoading(false);
    }
  };

  const submitParentMerge = async ({ parent1 }: { parent1: Parent }) => {
    if (!mergeTargetId || !mergeSourceId) return;
    setMergeLoading(true);
    setMergeError(null);
    try {
      await mergeParents({
        targetParentId: mergeTargetId,
        sourceParentId: mergeSourceId,
        mergedParent: parent1,
      });
      setMergeCandidates(null);
      setMergeTargetId(null);
      setMergeSourceId(null);
      const data = await fetchParents();
      setCachedParents(data);
      setParents(data);
    } catch (mergeFailure) {
      setMergeError(
        (mergeFailure as any)?.response?.data?.message ||
          t("parentMerge.mergeFailed", { defaultValue: "Could not merge the selected parents." })
      );
    } finally {
      setMergeLoading(false);
    }
  };

  if (loading) return showInitialSpinner ? <Spinner /> : null;
  if (error) return <ErrorPage message={error.message} status={error.status} onRetry={() => window.location.reload()} />;

  const filterGroups = [
    {
      title: t("family.parents.overview.email_filter", { defaultValue: "E-mail validation" }),
      filters: emailFilters,
      toggle: (value: string) => setEmailFilters((current) => toggleFilterValue(current, value as EmailFilter)),
      options: [
        { id: "invalid", label: t("admin.email_validation.status.invalid", { defaultValue: "Invalid" }) },
        { id: "suspicious", label: t("admin.email_validation.status.suspicious", { defaultValue: "Suspicious" }) },
        { id: "valid", label: t("admin.email_validation.status.valid", { defaultValue: "Valid" }) },
        { id: "unknown", label: t("common.unknown", { defaultValue: "Unknown" }) },
      ],
    },
    {
      title: t("family.parents.overview.portal_filter", { defaultValue: "Portal" }),
      filters: portalFilters,
      toggle: (value: string) => setPortalFilters((current) => toggleFilterValue(current, value as PortalFilter)),
      options: [
        { id: "active", label: t("family.parents.overview.portal_active", { defaultValue: "Active" }) },
        { id: "pending", label: t("family.parents.overview.portal_pending", { defaultValue: "Pending" }) },
        { id: "inactive", label: t("family.parents.overview.portal_inactive", { defaultValue: "Inactive" }) },
      ],
    },
    {
      title: t("family.parents.overview.newsletter_filter", { defaultValue: "Newsletter" }),
      filters: newsletterFilters,
      toggle: (value: string) => setNewsletterFilters((current) => toggleFilterValue(current, value as NewsletterFilter)),
      options: [
        { id: "subscribed", label: t("family.parents.overview.newsletter_subscribed", { defaultValue: "Subscribed" }) },
        { id: "unsubscribed", label: t("family.parents.overview.newsletter_unsubscribed", { defaultValue: "Unsubscribed" }) },
        { id: "opted_out", label: t("family.parents.overview.newsletter_opted_out", { defaultValue: "Opted out" }) },
        { id: "ineligible", label: t("family.parents.overview.newsletter_ineligible", { defaultValue: "Ineligible" }) },
      ],
    },
  ];

  return (
    <PageTemplate
      title={null}
      wrapperClassName="max-w-none"
    >
      {showRotateOverlay && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-slate-950/90 px-6 text-center text-white backdrop-blur">
          <p className="mb-2 text-lg font-bold">
            {t("family.parents.overview.rotate_title", { defaultValue: "Rotate your screen" })}
          </p>
          <p className="max-w-xs text-sm text-slate-200">
            {t("family.parents.overview.rotate_message", {
              defaultValue: "This page works better in landscape mode or on a larger screen.",
            })}
          </p>
        </div>
      )}

      <div className="mx-auto max-w-7xl rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6">
        <SlidePageTransition className="space-y-5">
        <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-sky-50 text-3xl text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-sky-950 dark:text-violet-100 sm:h-24 sm:w-24">
                <FaUsers aria-hidden="true" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("family.parents.overview.section_label", { defaultValue: "Family administration" })}
                </p>
                <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                  {t("family.parents.overview.title", { defaultValue: "Parents" })}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                  {t("family.parents.overview.subtitle", {
                    defaultValue: "Manage parent details, portal access and newsletter status.",
                  })}
                </p>
                {loading && parents.length > 0 ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Badge tone="neutral">
                      {t("common.refreshing", { defaultValue: "Refreshing" })}
                    </Badge>
                  </div>
                ) : null}
              </div>
            </div>

            {canManageFamilies ? (
              <div className="ml-auto shrink-0 lg:mr-36">
                <button
                  type="button"
                  onClick={() => navigateParentFlow(navigate, parentCreatePath, "forward")}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-3 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
                >
                  <FaPlus aria-hidden="true" />
                  {t("family.parents.overview.add_button", { defaultValue: "Add parent" })}
                </button>
              </div>
            ) : null}
          </div>

          <div
            className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex"
            aria-hidden="true"
          >
            <FaUsers className="text-[110px] text-violet-200/70 dark:text-violet-800/40" />
          </div>
        </section>

        <section className="sticky top-3 z-50 rounded-[24px] border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur-md dark:border-white/10 dark:bg-slate-900/95">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <label className="relative flex-1">
              <span className="sr-only">
                {t("family.parents.overview.search_parent", { defaultValue: "Search for a parent" })}
              </span>
              <FaSearch
                aria-hidden="true"
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t("family.parents.overview.search_parent", {
                  defaultValue: "Search for a parent...",
                })}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-950/50 dark:text-white dark:focus:border-indigo-400 dark:focus:ring-indigo-400/10"
              />
            </label>

            <div ref={filterPopoverRef} className="relative">
              <button
                type="button"
                onClick={() => setFilterOpen((open) => !open)}
                className={`inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl border text-sm font-bold transition focus:outline-none focus:ring-2 focus:ring-indigo-500 lg:w-auto ${
                  filterOpen || activeFilterCount > 0
                    ? "border-indigo-300 bg-indigo-600 text-white shadow-lg shadow-indigo-900/15 hover:bg-indigo-700 dark:border-indigo-400 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400"
                    : "border-slate-200 bg-white text-slate-700 shadow-sm hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 dark:border-white/10 dark:bg-slate-950/50 dark:text-slate-200"
                } ${activeFilterCount > 0 ? "pr-10" : ""}`}
                aria-expanded={filterOpen}
              >
                <span className="inline-flex h-full items-center justify-center gap-2 px-5">
                  <FaFilter aria-hidden="true" />
                  {t("common.filter", { defaultValue: "Filter" })}
                </span>
                {activeFilterCount > 0 && (
                  <span className="mr-1 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs text-white">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    clearFilters();
                    setFilterOpen(false);
                  }}
                  className="absolute right-1 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl text-white transition hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/70"
                  aria-label={t("common.clear", { defaultValue: "Clear" })}
                >
                  <FaTimes aria-hidden="true" />
                </button>
              )}

              {filterOpen && (
                <div className="absolute right-0 z-[70] mt-3 w-[min(360px,calc(100vw-2rem))] rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_24px_60px_rgba(15,23,42,0.18)] dark:border-white/10 dark:bg-slate-900">
                  {filterGroups.map((group) => (
                    <div
                      key={group.title}
                      className="border-b border-slate-200/80 py-4 first:pt-0 last:border-b-0 dark:border-white/10"
                    >
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {group.title}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {group.options.map((option) => {
                          const active = group.filters.includes(option.id as never);
                          return (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => group.toggle(option.id)}
                              className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                                active
                                  ? "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-400/40 dark:bg-indigo-400/10 dark:text-indigo-200"
                                  : "border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
                              }`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  <div className="mt-4 flex justify-between gap-2">
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="rounded-full px-4 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
                    >
                      {t("common.clear", { defaultValue: "Clear" })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFilterOpen(false)}
                      className="rounded-full bg-indigo-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                    >
                      {t("common.close", { defaultValue: "Close" })}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {canManageFamilies && mergeTargetId && mergeSourceId && (
              <button
                type="button"
                onClick={compareParents}
                disabled={mergeLoading}
                className="inline-flex h-12 w-full shrink-0 items-center justify-center rounded-2xl bg-indigo-600 px-5 text-sm font-bold text-white shadow-lg shadow-indigo-900/15 transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:cursor-wait disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400 lg:w-auto"
              >
                {mergeLoading
                  ? t("common.loading", { defaultValue: "Loading..." })
                  : t("parentMerge.compare", { defaultValue: "Compare" })}
              </button>
            )}
          </div>
        </section>

        {mergeError && (
          <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-200">
            {mergeError}
          </div>
        )}

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          <div className="flex flex-col gap-2 border-b border-slate-200 px-5 py-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                {t("family.parents.overview.list_label", { defaultValue: "Parents" })}
              </p>
              <h2 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                {t("family.parents.overview.list_title", { defaultValue: "Parent overview" })}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="neutral">
                {t("common.result_count", {
                  count: filteredParents.length,
                  defaultValue: "{{count}} results",
                })}
              </Badge>
              {activeFilterCount > 0 ? (
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {t("family.parents.overview.filtered_result", {
                    defaultValue: "Filtered from {{count}} total",
                    count: parents.length,
                  })}
                </span>
              ) : null}
            </div>
          </div>

          {filteredParents.length > 0 ? (
            <div className="divide-y divide-slate-200/80 dark:divide-white/10">
              {filteredParents.map((parent) => {
                const backend = getBackendEmailAssessment(parent);
                const titleParts = backend
                  ? [
                      ...backend.reasons,
                      backend.suggestion ? `Example: ${backend.suggestion}` : "",
                      backend.source ? `Source: ${backend.source}` : "",
                    ].filter(Boolean)
                  : [];
                const title = titleParts.join(" • ") || undefined;
                const parentName = [parent.firstName, parent.lastName].filter(Boolean).join(" ");
                const isMergeTarget = mergeTargetId === parent.id;
                const isMergeSource = mergeSourceId === parent.id;
                const isSelectedForMerge = isMergeTarget || isMergeSource;

                return (
                  <article
                    key={parent.id}
                    className="group grid cursor-pointer gap-4 px-5 py-4 transition hover:bg-slate-50 dark:hover:bg-white/[0.035] lg:grid-cols-[minmax(240px,1.05fr)_minmax(220px,0.9fr)_150px_170px_auto] lg:items-center"
                    onClick={() => navigateParentFlow(navigate, parentDetailsPath(parent.id), "forward")}
                  >
                    <div className="flex min-w-0 items-center gap-4">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleMergeParent(parent.id);
                        }}
                        disabled={!canManageFamilies || (Boolean(mergeTargetId && mergeSourceId) && !isSelectedForMerge)}
                        aria-pressed={isSelectedForMerge}
                        aria-label={
                          isSelectedForMerge
                            ? t("parentMerge.deselectParent", {
                                defaultValue: "Remove {{name}} from merge selection",
                                name: parentName,
                              })
                            : t("parentMerge.selectParentForMerge", {
                                defaultValue: "Select {{name}} for merge",
                                name: parentName,
                              })
                        }
                        className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border text-sm font-bold transition focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
                          isSelectedForMerge
                            ? "border-indigo-600 bg-indigo-600 text-white shadow-md ring-2 ring-indigo-200 dark:border-indigo-300 dark:bg-indigo-500 dark:ring-indigo-400/30"
                            : "border-indigo-100 bg-indigo-50 text-indigo-700 hover:border-indigo-300 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-indigo-400/20 dark:bg-indigo-400/10 dark:text-indigo-200"
                        }`}
                      >
                        {initialsFor(parent)}
                        {isSelectedForMerge && (
                          <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-emerald-500 px-1 text-[10px] font-black text-white dark:border-slate-900">
                            {isMergeTarget ? "A" : "B"}
                          </span>
                        )}
                      </button>
                      <div className="min-w-0">
                        <p className="truncate text-base font-semibold text-slate-950 dark:text-white">
                          {parentName || "—"}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {t("family.parents.overview.parent_record", { defaultValue: "Parent record" })}
                        </p>
                      </div>
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                        <FaEnvelope className="shrink-0 text-indigo-500 dark:text-indigo-300" aria-hidden="true" />
                        <span className="truncate" title={parent.email || ""}>
                          {parent.email || "—"}
                        </span>
                        <CopyButton
                          value={parent.email}
                          label={t("common.copy_email", { defaultValue: "Copy email address" })}
                        />
                      </div>
                      {backend?.status === "invalid" && (
                        <div className="mt-2">
                          <Badge tone="danger" title={title}>
                            {t("admin.email_validation.status.invalid", { defaultValue: "Invalid format" })}
                          </Badge>
                        </div>
                      )}
                      {backend?.status === "suspicious" && (
                        <div className="mt-2">
                          <Badge tone="warn" title={title}>
                            {t("admin.email_validation.status.suspicious", {
                              defaultValue: "Suspicious email",
                            })}
                          </Badge>
                        </div>
                      )}
                    </div>

                    <div>
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 lg:hidden">
                        {t("family.parents.overview.portal_filter", { defaultValue: "Portal" })}
                      </p>
                      <PortalBadge parent={parent} />
                    </div>

                    <div>
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 lg:hidden">
                        {t("family.parents.overview.newsletter_filter", { defaultValue: "Newsletter" })}
                      </p>
                      <NewsletterBadge parent={parent} />
                    </div>

                    <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigateParentFlow(navigate, parentDetailsPath(parent.id), "forward");
                        }}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                      >
                        {t("common.actions.view", { defaultValue: "View" })}
                        <FaArrowRight aria-hidden="true" />
                      </button>

                      {canManageFamilies && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigateParentFlow(navigate, parentEditPath(parent.id), "forward");
                          }}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                          aria-label={t("common.actions.edit", { defaultValue: "Edit" })}
                        >
                          <FaPen aria-hidden="true" />
                        </button>
                      )}

                      {canManageFamilies && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            confirmDeleteParent(parent.id);
                          }}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-red-100 bg-red-50 text-red-600 shadow-sm transition hover:border-red-200 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-200"
                          aria-label={t("common.actions.delete", { defaultValue: "Delete" })}
                        >
                          <FaTrash aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-[22px] bg-slate-100 text-2xl text-slate-400 dark:bg-white/5 dark:text-slate-500">
                <FaShieldAlt aria-hidden="true" />
              </span>
              <h3 className="mt-5 text-lg font-bold text-slate-950 dark:text-white">
                {t("family.parents.overview.none_available", { defaultValue: "No parents found" })}
              </h3>
              <p className="mt-2 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">
                {t("family.parents.overview.none_available_hint", {
                  defaultValue: "Adjust your search or filters to see more results.",
                })}
              </p>
            </div>
          )}
        </section>
        </SlidePageTransition>
      </div>

      <Modal
        show={showConfirm}
        title={t("common.modal.are_you_sure")}
        message={t("common.modal.action_cannot_be_undone")}
        isConfirm
        type="confirm"
        onConfirm={handleDeleteParent}
        onClose={() => {
          setShowConfirm(false);
          setParentToDelete(null);
        }}
      />
      <ParentMergeWizardModal
        mode="manual-parent-merge"
        show={Boolean(mergeCandidates)}
        existingParent1={mergeCandidates?.target.parent}
        newParent1={mergeCandidates?.source.parent}
        existingChildren={mergeCandidates?.target.children || []}
        newChildren={mergeCandidates?.source.children || []}
        targetRelations={mergeCandidates?.target || {}}
        sourceRelations={mergeCandidates?.source || {}}
        onSubmit={submitParentMerge}
        onClose={() => setMergeCandidates(null)}
      />
    </PageTemplate>
  );
};

export default Parents;
